import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Autocomplete,
  AvatarGroup,
  Backdrop,
  Box,
  Button,
  Collapse,
Card,
  Checkbox,
  Chip,
  Divider,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Modal,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import { useForm, Controller } from 'react-hook-form';
import _ from 'lodash';
import PropTypes from 'prop-types';
import * as actions from '../../redux/actions';
import api from '../../utils/functions/api';
import Loading from '../../components/Loading';
import { Error } from '../../components/Error';
import { SIDEBAR_NAV_ITEMS, SIDEBAR_MODULE_TITLE } from '../../constants/sidebarNavConfig';
import { showAlert, confirmAsync } from '../../utils/functions/globalDialog';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';
import EmployeeAvatar from '../../components/EmployeeAvatar';

/* ---------- constants ---------- */
const PALETTE = [
  { light: '#e3f2fd', main: '#1976d2' },
  { light: '#e8f5e9', main: '#2e7d32' },
  { light: '#f3e5f5', main: '#7b1fa2' },
  { light: '#fff8e1', main: '#e65100' },
  { light: '#fce4ec', main: '#c62828' },
  { light: '#e0f7fa', main: '#00838f' },
  { light: '#f9fbe7', main: '#558b2f' },
];

const defaultFormValue = { name: '', description: '' };

/** flatten all hrefs from SIDEBAR_NAV_ITEMS (module root + subs) */
const ALL_MENU_ITEMS = (() => {
  const items = [];
  SIDEBAR_NAV_ITEMS.forEach((item) => {
    if (item.sub && item.sub.length > 0) {
      item.sub.forEach((s) => items.push({ href: s.href, title: s.title, module: item.name }));
    } else {
      items.push({ href: item.href, title: item.title, module: item.name });
    }
  });
  return items;
})();

/** group menu items by module */
const MENU_BY_MODULE = _.groupBy(ALL_MENU_ITEMS, 'module');

function getDisplayName(emp) {
  return formatEmployeeThaiName(emp) || '-';
}

/* ---------- RoleCreateForm ---------- */
function RoleCreateForm({ onSubmit, onCancel }) {
  const { control, handleSubmit, formState: { errors } } = useForm({ defaultValues: defaultFormValue });
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
        <Controller
          name="name"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              {...field}
              label="ชื่อบทบาท"
              size="small"
              fullWidth
              required
              error={!!errors.name}
              helperText={errors.name && 'กรุณาใส่ชื่อบทบาท'}
            />
          )}
        />
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextField {...field} label="คำอธิบาย" size="small" fullWidth multiline rows={2} />
          )}
        />
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 1 }}>
          <Button variant="contained" type="submit" size="small">บันทึก</Button>
          <Button variant="outlined" size="small" onClick={onCancel}>ยกเลิก</Button>
        </Box>
      </Box>
    </form>
  );
}

/* ================================================================
   Main Component
================================================================ */
function RoleList({ title }) {
  const dispatch = useDispatch();
  const role = useSelector((state) => state.role);
  const employee = useSelector((state) => state.employee);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [selectedId, setSelectedId] = useState(null);
  const [activeTab, setActiveTab] = useState('members');
  const [createOpen, setCreateOpen] = useState(false);
  const [mobileShowDetail, setMobileShowDetail] = useState(false);

  // member assignment
  const [memberSearch, setMemberSearch] = useState('');
  const [memberOptions, setMemberOptions] = useState([]);
  const [memberLoading, setMemberLoading] = useState(false);

  // menu permissions (local draft before save)
  const [menuDraft, setMenuDraft] = useState([]);
  const [menuSaving, setMenuSaving] = useState(false);
  // โมดูลที่กางรายการเมนูย่อยอยู่ — ซ่อนไว้เป็นค่าเริ่มต้น เพราะส่วนใหญ่ติ๊กแค่เมนูหลัก
  const [openSubMenus, setOpenSubMenus] = useState([]);
  const toggleSubMenus = (mod) =>
    setOpenSubMenus((prev) => (prev.includes(mod) ? prev.filter((m) => m !== mod) : [...prev, mod]));

  const roles = role?.rows || [];
  const allEmployees = employee?.rows || [];

  const getMembersForRole = (rid) => allEmployees.filter((emp) => {
    const sr = emp?.system_role;
    const arr = Array.isArray(sr) ? sr : (sr ? [sr] : []);
    return arr.some((r) => String(r?._id || r?.id || r) === String(rid));
  });

  const selectedRole = roles.find((r) => (r.id || r._id) === selectedId);
  const selectedColor = selectedRole ? PALETTE[roles.indexOf(selectedRole) % PALETTE.length] : PALETTE[0];
  const selectedMembers = selectedId ? getMembersForRole(selectedId) : [];

  /* ---- init ---- */
  useEffect(() => {
    dispatch(actions.roleAll());
    dispatch(actions.employeeAll({ size: 9999, page: 1, activeOnly: false }));
  }, []);

  useEffect(() => {
    if (roles.length > 0 && !selectedId) {
      setSelectedId(roles[0].id || roles[0]._id);
    }
  }, [roles]);

  useEffect(() => {
    if (selectedRole) {
      setMenuDraft(selectedRole.allowed_menus || []);
      if (selectedRole.is_default && activeTab === 'members') {
        setActiveTab('menus');
      }
    }
  }, [selectedId, roles]);

  /* ---- employee search ---- */
  const searchDebounceRef = useRef(null);

  const doSearch = async (term) => {
    if (!term || term.length < 1) { setMemberOptions([]); return; }
    setMemberLoading(true);
    try {
      const { data } = await api.get(
        `${process.env.REACT_APP_API_URL}/employee?name=${encodeURIComponent(term)}&size=20&page=1`,
      );
      setMemberOptions(data.rows || []);
    } catch (_) {
      setMemberOptions([]);
    } finally {
      setMemberLoading(false);
    }
  };

  const handleSearchInput = (term) => {
    setMemberSearch(term);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => doSearch(term), 400);
  };

  /* ---- actions ---- */
  const handleCreate = async (data) => {
    await dispatch(actions.roleCreate(data));
    setCreateOpen(false);
    await dispatch(actions.roleAll());
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!(await confirmAsync('ยืนยันการลบบทบาทนี้? พนักงานที่มีบทบาทนี้จะถูกยกเลิกการกำหนดโดยอัตโนมัติ'))) return;
    try {
      await dispatch(actions.roleDelete(id));
      if (selectedId === id) { setSelectedId(null); setMobileShowDetail(false); }
      await dispatch(actions.roleAll());
      await dispatch(actions.employeeAll({ size: 9999, page: 1, activeOnly: false }));
    } catch (err) {
      showAlert(err.message || 'เกิดข้อผิดพลาด', { status: err?.response?.status });
    }
  };

  const handleSelectRole = (rid) => {
    setSelectedId(rid);
    if (isMobile) setMobileShowDetail(true);
  };

  const handleBackToList = () => setMobileShowDetail(false);

  const handleAssignEmployee = async (emp) => {
    if (!emp || !selectedId) return;
    try {
      await api.post(`${process.env.REACT_APP_API_URL}/role/${selectedId}/members`, {
        employee_id: emp.id || emp._id,
      });
      await dispatch(actions.employeeAll({ size: 9999, page: 1, activeOnly: false }));
    } catch (err) {
      showAlert('ไม่สามารถกำหนดบทบาทได้');
    }
  };

  const handleRemoveEmployee = async (emp) => {
    if (!(await confirmAsync(`ยืนยันการถอด ${getDisplayName(emp)} ออกจากบทบาทนี้?`))) return;
    try {
      await api.delete(`${process.env.REACT_APP_API_URL}/role/${selectedId}/members/${emp.id || emp._id}`);
      await dispatch(actions.employeeAll({ size: 9999, page: 1, activeOnly: false }));
    } catch (_) {
      showAlert('เกิดข้อผิดพลาด');
    }
  };

  // เมนูย่อยของโมดูลที่ได้ module key (เมนูหลัก) ได้ครบทุกอันรวมเมนูใหม่ในอนาคตอัตโนมัติ — ติ๊กออกเฉพาะเมนู
  // เก็บเป็น '!href' (ยกเว้น) ใน allowed_menus เดียวกัน ติ๊กกลับ = ลบ '!href' ออก
  // โมดูลที่ยังเป็นแบบเก่า (เก็บ href ทีละอัน ไม่มี module key) ยังทำงานเป็น whitelist เหมือนเดิมจนกว่าจะติ๊กเมนูหลัก
  const handleMenuToggle = (href, module) => {
    setMenuDraft((prev) => {
      const deny = `!${href}`;
      if (prev.includes(module)) {
        return prev.includes(deny) ? prev.filter((h) => h !== deny) : [...prev, deny];
      }

      return prev.includes(href) ? prev.filter((h) => h !== href) : [...prev, href];
    });
  };

  // ติ๊ก "เมนูหลัก" เก็บเป็น module key เดียว (เช่น 'ACCOUNTING') — เมนูย่อยทั้งหมดรวมที่เพิ่มใหม่ทีหลังเข้าถึงได้
  // ทันทีโดยไม่ต้องกลับมาติ๊กเพิ่ม (ดู roleGrantsHref ใน accessRight.js) กดซ้ำตอนมีเมนูที่ถูกยกเว้น = ล้างการยกเว้น
  // ให้ได้ครบก่อน กดอีกครั้งถึงจะถอดสิทธิ์ทั้งโมดูล
  const handleModuleToggle = (module) => {
    const moduleHrefs = (MENU_BY_MODULE[module] || []).map((m) => m.href);
    const denies = moduleHrefs.map((h) => `!${h}`);
    setMenuDraft((prev) => {
      const granted = prev.includes(module);
      const hasDenied = denies.some((d) => prev.includes(d));
      const clean = prev.filter((h) => h !== module && !moduleHrefs.includes(h) && !denies.includes(h));
      if (granted && hasDenied) {
        return _.uniq([...clean, module]);
      }
      if (granted || moduleHrefs.every((h) => prev.includes(h))) {
        return clean;
      }

      return _.uniq([...clean, module]);
    });
  };

  const handleSaveMenus = async () => {
    if (!selectedId) return;
    setMenuSaving(true);
    try {
      await dispatch(actions.rolePut(selectedId, { allowed_menus: menuDraft }));
      await dispatch(actions.roleAll());
    } catch (_) {
      showAlert('บันทึกไม่สำเร็จ');
    } finally {
      setMenuSaving(false);
    }
  };

  /* ================================================================
     Render helpers
  ================================================================ */
  const renderCreateModal = () => (
    <Modal
      open={createOpen}
      onClose={() => setCreateOpen(false)}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 400 }}
    >
      <Fade in={createOpen}>
        <Card sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '92vw', sm: 480 },
          maxWidth: 480, boxShadow: 24, p: { xs: 3, sm: 4 },
        }}>
          <Typography variant="h6" sx={{ pb: 1, fontWeight: 700 }}>สร้างบทบาทใหม่</Typography>
          <RoleCreateForm onSubmit={handleCreate} onCancel={() => setCreateOpen(false)} />
        </Card>
      </Fade>
    </Modal>
  );

  const renderRoleItem = (r, idx) => {
    const rid = r.id || r._id;
    const isSelected = rid === selectedId;
    const color = PALETTE[idx % PALETTE.length];
    const members = getMembersForRole(rid);
    return (
      <Box
        key={rid}
        onClick={() => handleSelectRole(rid)}
        sx={{
          cursor: 'pointer',
          borderLeft: isSelected ? `3px solid ${color.main}` : '3px solid transparent',
          backgroundColor: isSelected ? color.light : 'transparent',
          transition: 'all 0.15s ease',
          px: 2, py: 1.5,
          '&:hover': { bgcolor: isSelected ? color.light : 'grey.50' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontWeight: 700, letterSpacing: 0.4, color: isSelected ? color.main : '#212121', textTransform: 'uppercase' }}>
                {r.name}
              </Typography>
              {r.is_default && (
                <Chip label="DEFAULT" size="small" icon={<GroupsOutlinedIcon sx={{ fontSize: '12px !important' }} />}
                  sx={{ height: 16, fontSize: 9, fontWeight: 700, bgcolor: '#e8f5e9', color: '#2e7d32', '& .MuiChip-label': { px: 0.5 }, '& .MuiChip-icon': { ml: 0.5 } }} />
              )}
            </Box>
            <Typography variant="caption" sx={{ color: '#9e9e9e' }}>
              {r.is_default ? 'ทุก employee' : `${members.length} คน`}
            </Typography>
          </Box>
          {!r.is_default && (
            <Tooltip title="ลบ">
              <IconButton size="small" sx={{ opacity: 0.35, '&:hover': { opacity: 1, color: 'error.main' } }} onClick={(e) => handleDelete(rid, e)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {!r.is_default && members.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <AvatarGroup max={4} sx={{ justifyContent: 'flex-start', '& .MuiAvatar-root': { width: 24, height: 24, fontSize: 10 } }}>
              {members.map((emp) => (
                <Tooltip key={emp.id || emp._id} title={getDisplayName(emp)}>
                  <EmployeeAvatar employee={emp} size={24} />
                </Tooltip>
              ))}
            </AvatarGroup>
          </Box>
        )}
      </Box>
    );
  };

  const renderLeftPanel = () => (
    <Box sx={{
      width: { md: 260 },
      minWidth: { md: 260 },
      borderRight: { md: '1px solid #e0e0e0' },
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <LockOutlinedIcon fontSize="small" sx={{ color: '#757575' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.5, color: '#424242' }}>
            ROLES ({roles.length})
          </Typography>
        </Box>
      </Box>
      <Divider />
      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        {roles.length === 0 && (
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">ยังไม่มีบทบาท</Typography>
          </Box>
        )}
        {roles.map((r, idx) => renderRoleItem(r, idx))}
      </Box>
    </Box>
  );

  const renderMembersTab = () => (
    <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>เพิ่มสมาชิก</Typography>
        <Autocomplete
          size="small"
          options={memberOptions}
          loading={memberLoading}
          inputValue={memberSearch}
          value={null}
          filterOptions={(x) => x}
          onInputChange={(_, v, reason) => {
            if (reason === 'input') handleSearchInput(v);
            if (reason === 'clear') { setMemberSearch(''); setMemberOptions([]); }
          }}
          getOptionLabel={(opt) => getDisplayName(opt)}
          isOptionEqualToValue={(opt, val) => (opt?.id || opt?._id) === (val?.id || val?._id)}
          noOptionsText={memberSearch.length < 1 ? 'พิมพ์ชื่อพนักงานเพื่อค้นหา' : 'ไม่พบพนักงาน'}
          onChange={(_, emp) => {
            if (emp) {
              handleAssignEmployee(emp);
              setMemberSearch('');
              setMemberOptions([]);
            }
          }}
          renderOption={(props, opt) => (
            <li {...props} key={opt.id || opt._id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmployeeAvatar employee={opt} size={28} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{getDisplayName(opt)}</Typography>
                  {opt.department?.name && <Typography variant="caption" color="text.secondary">{opt.department.name}</Typography>}
                </Box>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} placeholder="พิมพ์ชื่อพนักงานเพื่อเพิ่มในบทบาทนี้" />
          )}
        />
      </Box>
      <Divider />
      <Box sx={{ mt: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          สมาชิก ({selectedMembers.length})
        </Typography>
        {selectedMembers.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#bdbdbd', fontStyle: 'italic' }}>ยังไม่มีสมาชิก</Typography>
        ) : (
          <List dense disablePadding>
            {selectedMembers.map((emp) => (
              <ListItem
                key={emp.id || emp._id}
                disableGutters
                sx={{ py: 0.5 }}
                secondaryAction={
                  <Tooltip title="ถอดออกจากบทบาท">
                    <IconButton size="small" sx={{ opacity: 0.4, '&:hover': { opacity: 1, color: 'error.main' } }} onClick={() => handleRemoveEmployee(emp)}>
                      <PersonRemoveOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemAvatar sx={{ minWidth: 44 }}>
                  <EmployeeAvatar employee={emp} size={34} />
                </ListItemAvatar>
                <ListItemText
                  primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>{getDisplayName(emp)}</Typography>}
                  secondary={emp.department?.name || emp.position || undefined}
                  secondaryTypographyProps={{ variant: 'caption', color: '#9e9e9e' }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );

  const renderMenusTab = () => {
    const modules = Object.keys(MENU_BY_MODULE);
    // นับจำนวนเมนูจริงที่เข้าถึงได้ (แตก module key ที่ติ๊กทั้งโมดูลออกมานับด้วย)
    // ไว้ใช้แสดงผลใน caption เท่านั้น
    const expandedMenuCount = modules.reduce((sum, mod) => {
      const items = MENU_BY_MODULE[mod] || [];
      return sum + (menuDraft.includes(mod) ? items.length : items.filter((it) => menuDraft.includes(it.href)).length);
    }, 0);
    return (
      <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Box sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          pb: 1.5,
          flexWrap: 'wrap',
          gap: 1,
        }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>สิทธิ์เข้าถึงเมนู</Typography>
            <Typography variant="caption" sx={{ color: '#9e9e9e' }}>
              {expandedMenuCount === 0
                ? 'ไม่จำกัด (เข้าได้ทุกเมนูตามสิทธิ์แผนก)'
                : `${expandedMenuCount} เมนูที่เลือก`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="outlined" sx={{ minWidth: 72 }} onClick={() => setMenuDraft([])}>
              ล้างทั้งหมด
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<SaveOutlinedIcon />}
              onClick={handleSaveMenus}
              disabled={menuSaving}
            >
              {menuSaving ? 'กำลังบันทึก...' : 'บันทึก'}
            </Button>
          </Box>
          <Divider sx={{ position: 'absolute', left: 0, right: 0, bottom: 0 }} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {modules.map((mod) => {
            const items = MENU_BY_MODULE[mod] || [];
            const moduleHrefs = items.map((m) => m.href);
            const moduleGranted = menuDraft.includes(mod);
            const isItemChecked = (href) => (moduleGranted ? !menuDraft.includes(`!${href}`) : menuDraft.includes(href));
            const checkedCount = moduleHrefs.filter(isItemChecked).length;
            const allChecked = checkedCount === moduleHrefs.length;
            const someChecked = !allChecked && checkedCount > 0;
            return (
              <Paper key={mod} variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
                <ListItemButton dense onClick={() => handleModuleToggle(mod)} sx={{ bgcolor: '#fafafa', py: 1 }}>
                  <Checkbox
                    size="small"
                    checked={allChecked}
                    indeterminate={someChecked}
                    onChange={() => handleModuleToggle(mod)}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ p: 0, mr: 1.5 }}
                  />
                  <ListItemText
                    primary={<Typography variant="body2" sx={{ fontWeight: 700 }}>{SIDEBAR_MODULE_TITLE[mod] || mod}</Typography>}
                    secondary={<Typography variant="caption" color="text.secondary">{checkedCount}/{moduleHrefs.length} เมนู</Typography>}
                  />
                  <Button
                    size="small"
                    variant={openSubMenus.includes(mod) ? 'contained' : 'outlined'}
                    startIcon={<TuneIcon fontSize="small" />}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSubMenus(mod);
                    }}
                    sx={{ flexShrink: 0, ml: 1 }}
                  >
                    ตั้งค่าเมนูย่อย
                  </Button>
                </ListItemButton>
                <Collapse in={openSubMenus.includes(mod)} unmountOnExit>
                <Divider />
                {items.map((item) => (
                  <ListItemButton key={item.href} dense onClick={() => handleMenuToggle(item.href, mod)} sx={{ pl: 4.5, py: 0.5 }}>
                    <Checkbox
                      size="small"
                      checked={isItemChecked(item.href)}
                      onChange={() => handleMenuToggle(item.href, mod)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ p: 0, mr: 1.5 }}
                    />
                    <ListItemText
                      primary={<Typography variant="body2">{item.title}</Typography>}
                      secondary={<Typography variant="caption" sx={{ color: '#bdbdbd' }}>{item.href}</Typography>}
                    />
                  </ListItemButton>
                ))}
                </Collapse>
              </Paper>
            );
          })}
        </Box>
      </Box>
    );
  };

  const renderRightPanel = () => {
    if (!selectedRole) {
      return (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
          <Box sx={{ textAlign: 'center' }}>
            <PeopleOutlineIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="body2">เลือกบทบาทเพื่อดูรายละเอียด</Typography>
          </Box>
        </Box>
      );
    }
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Back button on mobile */}
        {isMobile && (
          <Box sx={{ px: 2, pt: 1.5, pb: 0 }}>
            <Button size="small" startIcon={<ArrowBackIcon />} onClick={handleBackToList} sx={{ color: 'text.secondary' }}>
              รายการบทบาท
            </Button>
          </Box>
        )}

        {/* Role header */}
        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.3, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              {selectedRole.name?.toUpperCase()}
            </Typography>
            {selectedRole.is_default && (
              <Chip
                label="ติดตัวทุก Employee"
                size="small"
                icon={<GroupsOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, fontSize: 11 }}
              />
            )}
            {(selectedRole.allowed_menus?.length || 0) > 0 && (
              <Chip
                label={`${selectedRole.allowed_menus.length} เมนู`}
                size="small"
                sx={{ bgcolor: selectedColor.light, color: selectedColor.main, fontWeight: 600, fontSize: 11 }}
              />
            )}
          </Box>
          {selectedRole.is_default && (
            <Typography variant="caption" sx={{ color: '#2e7d32', mt: 0.5, display: 'block' }}>
              บทบาทนี้ติดตัวพนักงานทุกคนโดยอัตโนมัติ — ลบไม่ได้และถอดออกจากพนักงานไม่ได้
            </Typography>
          )}
          {selectedRole.description && (
            <Typography variant="body2" sx={{ color: '#757575', mt: 0.5 }}>{selectedRole.description}</Typography>
          )}
        </Box>

        {/* Tabs */}
        <Box sx={{ display: 'flex', borderBottom: '1px solid #e0e0e0', px: 2 }}>
          {[
            ...(!selectedRole.is_default ? [{ key: 'members', label: 'สมาชิก', icon: <PeopleOutlineIcon sx={{ fontSize: 16 }} /> }] : []),
            { key: 'menus', label: 'เมนูที่เข้าถึงได้', icon: <MenuOpenIcon sx={{ fontSize: 16 }} /> },
          ].map((tab) => (
            <Box
              key={tab.key}
              component="button"
              onClick={() => setActiveTab(tab.key)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.75,
                px: { xs: 1.5, sm: 2 }, py: 1.25,
                fontSize: { xs: 13, sm: 14 }, fontWeight: 500,
                border: 'none', background: 'none', cursor: 'pointer',
                borderBottom: activeTab === tab.key ? '2px solid #5c6bc0' : '2px solid transparent',
                color: activeTab === tab.key ? '#5c6bc0' : '#757575',
                '&:hover': { color: '#212121' },
                whiteSpace: 'nowrap',
              }}
            >
              {tab.icon}
              {tab.label}
              {tab.key === 'members' && selectedMembers.length > 0 && (
                <Box component="span" sx={{ ml: 0.5, fontSize: 11, bgcolor: '#f5f5f5', color: '#616161', borderRadius: '999px', px: 0.75, py: 0.25 }}>
                  {selectedMembers.length}
                </Box>
              )}
            </Box>
          ))}
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {activeTab === 'members' && !selectedRole.is_default && renderMembersTab()}
          {activeTab === 'menus' && renderMenusTab()}
        </Box>
      </Box>
    );
  };

  if (role?.isLoading && !role?.rows) return <Loading />;
  if (!role?.isLoading && !role?.isCompleted) return <Error message={role?.message} />;

  /* ---- Mobile: master-detail navigation ---- */
  if (isMobile) {
    return (
      <Box>
        {renderCreateModal()}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{title}</Typography>
          {!mobileShowDetail && (
            <Button variant="contained" startIcon={<AddIcon />} size="small" onClick={() => setCreateOpen(true)}>
              สร้างบทบาท
            </Button>
          )}
        </Box>

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', minHeight: 400 }}>
          {!mobileShowDetail ? (
            /* List view */
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 2, py: 1.5, borderBottom: '1px solid #e0e0e0' }}>
                <LockOutlinedIcon fontSize="small" sx={{ color: '#757575' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.5, color: '#424242' }}>
                  ROLES ({roles.length})
                </Typography>
              </Box>
              {roles.length === 0 ? (
                <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">ยังไม่มีบทบาท</Typography>
                </Box>
              ) : (
                roles.map((r, idx) => renderRoleItem(r, idx))
              )}
            </Box>
          ) : (
            /* Detail view */
            renderRightPanel()
          )}
        </Paper>
      </Box>
    );
  }

  /* ---- Desktop: side-by-side ---- */
  return (
    <Box>
      {renderCreateModal()}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>{title}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} size="small" onClick={() => setCreateOpen(true)}>
          สร้างบทบาท
        </Button>
      </Box>

      <Paper
        variant="outlined"
        sx={{ display: 'flex', borderRadius: 2, overflow: 'hidden', height: 'calc(100vh - 180px)', minHeight: 400 }}
      >
        {renderLeftPanel()}
        {renderRightPanel()}
      </Paper>
    </Box>
  );
}

RoleList.propTypes = {
  title: PropTypes.string,
};

RoleList.defaultProps = {
  title: 'บทบาท',
};

export default RoleList;
